import React, { useState, useRef, useEffect } from 'react';
import '../styles/activities.css';
import ContentRenderer from '../components/ContentRenderer';
import StreamingText from '../components/StreamingText';
import supabaseLocalData from '../fake_data/supabase-local.json';
import { generateSectionContent, generateSectionVisual } from '../context/engine.ts';
import { supabase } from '../integrations/client.ts';

const LOADING_MESSAGES = [
    "Prepping your lesson...",
    "Cooking up some knowledge...",
    "Brewing educational goodness...",
    "Crafting your content...",
    "Assembling the pieces...",
    "Getting things ready...",
    "Mixing up the magic...",
    "Preparing something special..."
];

function CourseContent({ onToggleHandsOn, handsOnVisible, currentCourse, isAuthenticated, selectedSection }) {
    const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState(LOADING_MESSAGES[0]);
    const [sectionContentCache, setSectionContentCache] = useState({});
    const [isStreaming, setIsStreaming] = useState(false);
    const [streamingSectionId, setStreamingSectionId] = useState(null);
    const [visualCache, setVisualCache] = useState({}); // Cache for section visuals
    const [isLoadingVisual, setIsLoadingVisual] = useState(false); // Loading state for visuals
    const contentAreaRef = useRef(null);

    // Check if user has scrolled to the bottom
    const handleScroll = () => {
        if (contentAreaRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = contentAreaRef.current;
            const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px threshold
            setHasScrolledToBottom(isAtBottom);
        }
    };

    useEffect(() => {
        const contentArea = contentAreaRef.current;
        if (contentArea) {
            contentArea.addEventListener('scroll', handleScroll);
            // Check initial state in case content is short enough to not require scrolling
            handleScroll();

            return () => {
                contentArea.removeEventListener('scroll', handleScroll);
            };
        }
    }, [selectedSection]); // Reset scroll when section changes

    // Generate content when section is selected
    useEffect(() => {
        const checkAndGenerateContent = async () => {
            if (!selectedSection?.id || !currentCourse || !isAuthenticated) {
                return;
            }

            const sectionId = selectedSection.id;

            // Check if content exists in cache
            if (sectionContentCache[sectionId]) {
                return;
            }

            // Check if section has content
            if (selectedSection.content && selectedSection.content.trim() !== '') {
                // Content exists, cache it
                setSectionContentCache(prev => ({
                    ...prev,
                    [sectionId]: selectedSection.content
                }));
                return;
            }

            // No content exists, generate it
            try {
                setIsGenerating(true);

                // Pick a random loading message
                const randomMessage = LOADING_MESSAGES[Math.floor(Math.random() * LOADING_MESSAGES.length)];
                setLoadingMessage(randomMessage);


                const content = await generateSectionContent(
                    sectionId,
                    currentCourse.name,
                    selectedSection.chapter_name,
                    selectedSection.name,
                    selectedSection.chapter_order || 1
                );

                // Update the section with generated content
                selectedSection.content = content;

                // Cache the content
                setSectionContentCache(prev => ({
                    ...prev,
                    [sectionId]: content
                }));

                setIsGenerating(false);

                // Start streaming animation for this section
                setIsStreaming(true);
                setStreamingSectionId(sectionId);
            } catch (error) {
                setIsGenerating(false);
            }
        };

        checkAndGenerateContent();
    }, [selectedSection?.id]); // Only depend on section ID change

    // VISUAL GENERATION DISABLED - Backlogged for premium users due to bugs
    // TODO: Re-enable when visual generation is stable and ready for premium users
    /*
    // Lazy load visual for the section AFTER content is displayed
    useEffect(() => {
        const loadSectionVisual = async () => {
            if (!selectedSection?.id || !selectedSection?.content || !isAuthenticated) {
                return;
            }

            const sectionId = selectedSection.id;

            // Check if visual exists in cache
            if (visualCache[sectionId] !== undefined) {
                return; // Already loaded or checked
            }

            // Check if visual already exists in database (from template, Python generation, or previous load)
            try {
                const { data: sectionData } = await supabase
                    .from('sections')
                    .select('visual_code')
                    .eq('id', sectionId)
                    .single();

                if (sectionData?.visual_code) {
                    // Visual exists in DB - cache it immediately (instant load)
                    // This includes visuals from course_templates (pre-generated by orb-py)
                    console.log('Visual loaded from database (instant)');
                    setVisualCache(prev => ({
                        ...prev,
                        [sectionId]: sectionData.visual_code
                    }));
                    return;
                }

                // No visual in DB - check if this is a template course
                // Template courses should NEVER generate new visuals, regardless of subscription
                if (currentCourse?.is_from_template === true || currentCourse?.template_id) {
                    // If visual_code doesn't exist in template, no visual should be shown
                    setVisualCache(prev => ({
                        ...prev,
                        [sectionId]: null
                    }));
                    return;
                }

                // Generate visual for user-created courses only
                console.log('User-created course - generating visual for section...');
                setIsLoadingVisual(true);

                const visualCode = await generateSectionVisual(sectionId, selectedSection.content);

                if (visualCode) {
                    console.log('Visual generated and saved to database');
                    setVisualCache(prev => ({
                        ...prev,
                        [sectionId]: visualCode
                    }));
                } else {
                    // No visual needed for this section
                    setVisualCache(prev => ({
                        ...prev,
                        [sectionId]: null
                    }));
                }

                setIsLoadingVisual(false);
            } catch (error) {
                console.error('Error loading/generating visual:', error);
                setIsLoadingVisual(false);
                // Mark as checked to prevent retry loops
                setVisualCache(prev => ({
                    ...prev,
                    [sectionId]: null
                }));
            }
        };

        // Only load visual after content is ready
        if (!isGenerating && !isStreaming) {
            loadSectionVisual();
        }
    }, [selectedSection?.id, selectedSection?.content, isGenerating, isStreaming, isAuthenticated, currentCourse?.is_from_template]);
    */

    // Generate course content from selected section
    const buildSectionMarkdown = () => {
        if (!isAuthenticated) {
            return `# Welcome to the Learning Platform

Please **sign in** to access course content and start your learning journey.

## What You'll Get Access To

- **Interactive Course Content** - Rich text, equations, and diagrams
- **Chapter Organization** - Structured learning with clear progression  
- **Practice Activities** - Hands-on exercises to reinforce learning
- **Progress Tracking** - Keep track of your learning achievements

*Sign in above to get started!*`;
        }

        if (!currentCourse) {
            return `# Welcome to the Learning Platform

Please select a course from your **Course History** to begin learning.

## How to Get Started

1. **Open Course History** - Click the hamburger menu (☰) in the top left
2. **Select a Course** - Click on any course from your completed courses list
3. **Choose a Section** - Click "Chapters" to browse and select specific sections
4. **Start Learning** - The section content will appear here

## Features Available

- **Interactive Content** - Rich text, math equations, and diagrams
- **Chapter Navigation** - Browse through course chapters and sections  
- **Practice Mode** - Hands-on activities to reinforce learning
- **Progress Tracking** - Keep track of your learning journey

*Select a course to continue your learning journey!*`;
        }

        if (!selectedSection) {
            // Build chapters data from supabase-local.json
            const chaptersData = currentCourse ?
                supabaseLocalData.chapters
                    .filter(chapter => chapter.course_id === currentCourse.id)
                    .sort((a, b) => a.order - b.order)
                    .map(chapter => {
                        // Get sections for this chapter
                        const sections = supabaseLocalData.sections
                            .filter(section => section.chapter_id === chapter.id)
                            .sort((a, b) => a.order - b.order);

                        return {
                            chapter_name: chapter.name,
                            sections: sections
                        };
                    }) : [];

            return `# ${currentCourse.name}

Welcome to **${currentCourse.name}**! Please select a section from the chapters to begin learning.

## Available Chapters

${chaptersData.map((chapter, index) =>
    `### ${index + 1}. ${chapter.chapter_name}
${chapter.sections.map((section, sIndex) =>
    `   ${sIndex + 1}. ${section.name}`
).join('\n')}
`).join('\n')}

## How to Navigate

1. **Click "Chapters"** in the header above
2. **Expand a chapter** by clicking on it
3. **Select a section** to view its content here
4. **Complete the reading** to unlock practice mode

*Click "Chapters" above to select a section and start learning!*`;
        }

        // Generate content for the selected section
        let content = `# ${selectedSection.name}\n\n`;

        // Add chapter context
        //content += `*Chapter: ${selectedSection.chapter_name}*\n\n`;
        //content += `---\n\n`;

        // Add the main section content
        content += `${selectedSection.content}\n\n`;

        // Add activity information if available (not implemented in current data structure)
        // if (selectedSection.section_activity_type || selectedSection.section_activity_content) {
        //     content += `---\n\n## Practice Activity\n\n`;
        //
        //     if (selectedSection.section_activity_type) {
        //         content += `**Activity Type:** ${selectedSection.section_activity_type}\n\n`;
        //     }
        //
        //     if (selectedSection.section_activity_content) {
        //         content += `${selectedSection.section_activity_content}\n\n`;
        //     } else {
        //         content += `*Practice activities will be available here once configured.*\n\n`;
        //     }
        // }
        
        // Add navigation hint
        // content += `---\n\n*Use the **Chapters** menu to navigate to other sections.*\n\n`;
        // content += `**You've reached the end of this section! The practice button should now be enabled.**`;
        
        return content;
    };

    // Show loading state if generating
    if (isGenerating) {
        return (
            <div className="course-content-container">
                <div className="content-area" ref={contentAreaRef}>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        padding: '40px'
                    }}>
                        <div style={{
                            fontSize: '18px',
                            fontWeight: '500',
                            color: '#000',
                            animation: 'shimmer 2s infinite',
                            background: 'linear-gradient(90deg, #000 0%, #666 50%, #000 100%)',
                            backgroundSize: '200% auto',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text'
                        }}>
                            {loadingMessage}
                        </div>
                    </div>
                </div>
                <div className="course-content-footer">
                    <button className="hands-on-toggle-btn disabled" disabled>
                        Loading...
                    </button>
                </div>
                <style>{`
                    @keyframes shimmer {
                        0% { background-position: -200% center; }
                        100% { background-position: 200% center; }
                    }
                `}</style>
            </div>
        );
    }

    const sectionContent = buildSectionMarkdown();

    // Check if this section should be streamed
    const shouldStream = isStreaming && streamingSectionId === selectedSection?.id;

    // VISUAL RENDERING DISABLED - Backlogged for premium users
    // Get visual code for current section (if available)
    const currentVisualCode = null; // Temporarily disabled: selectedSection?.id ? visualCache[selectedSection.id] : null;

    return (
        <div className="course-content-container">
                {/* <div className="course-content-header">
                <h2>
                    {!isAuthenticated ? 'Course Content' :
                     !currentCourse ? 'Course Content' :
                     !selectedSection ? currentCourse.name :
                     selectedSection.section_name}
                </h2>
                {selectedSection && (
                    <div style={{
                        fontSize: '14px',
                        color: '#666',
                        marginTop: '4px',
                        fontStyle: 'italic'
                    }}>
                        {currentCourse.name} → {selectedSection.chapter_name}
                    </div>
                )}
            </div> */}
            <div className="content-area" ref={contentAreaRef}>
                {shouldStream ? (
                    <StreamingText
                        content={sectionContent}
                        sources={selectedSection?.sources}
                        visualCode={currentVisualCode}
                        onComplete={() => {
                            setIsStreaming(false);
                            setStreamingSectionId(null);
                        }}
                        streamingSpeed={15}
                    />
                ) : (
                    <>
                        <ContentRenderer
                            content={sectionContent}
                            sources={selectedSection?.sources}
                            visualCode={currentVisualCode}
                        />
                        {/* VISUAL LOADING INDICATOR DISABLED - No visuals are being generated
                        {isLoadingVisual && (
                            <div style={{
                                marginTop: '20px',
                                padding: '15px',
                                backgroundColor: '#f0f8ff',
                                borderRadius: '8px',
                                textAlign: 'center',
                                color: '#666',
                                fontSize: '14px'
                            }}>
                                Generating interactive visualization...
                            </div>
                        )}
                        */}
                    </>
                )}
            </div>
            <div className="course-content-footer">
                <button
                    className={`hands-on-toggle-btn ${!hasScrolledToBottom || !selectedSection || !isAuthenticated || isStreaming || isGenerating ? 'disabled' : ''}`}
                    onClick={hasScrolledToBottom && selectedSection && isAuthenticated && !isStreaming && !isGenerating ? onToggleHandsOn : undefined}
                    disabled={!hasScrolledToBottom || !selectedSection || !isAuthenticated || isStreaming || isGenerating}
                    title={
                        !isAuthenticated ? 'Sign in to access practice mode' :
                        !selectedSection ? 'Select a section to access practice mode' :
                        isStreaming ? 'Wait for content to finish streaming' :
                        isGenerating ? 'Wait for content to finish generating' :
                        !hasScrolledToBottom ? 'Scroll to the end to unlock practice mode' : ''
                    }
                >
                    <span className="desktop-button-text">
                        {!isAuthenticated ? 'Sign In Required' :
                         !selectedSection ? 'Select Section First' :
                         !hasScrolledToBottom ? 'Complete Reading First' :
                         handsOnVisible ? 'Hide Practice' : 'Show Practice'}
                    </span>
                    <span className="mobile-button-text">
                        {!isAuthenticated ? 'Sign In' :
                         !selectedSection ? 'Select Section' :
                         !hasScrolledToBottom ? 'Keep Reading' :
                         handsOnVisible ? '← Back to Content' : 'Practice →'}
                    </span>
                </button>
            </div>
        </div>
    );
}

export default CourseContent;